import React, { useState, useEffect, useCallback } from "react";
import { toastFail } from "../../config";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "../../ds2/components";
import "../../index.css";
import "./SlackLanding.css";
import { apiSlackAuth } from "../../services/OAuthService";
import { CircularProgress } from "@mui/material";
import { apiGetUser } from "../../api";
import { selectUser, setUser } from "../../redux/features/appData";
import { TRACK_EVENTS, trackAnalyticsEvent } from "../../hooks/useAnalytics";
import toast from "react-hot-toast";

export default function SlackLanding() {
  const storeUser = useSelector(selectUser);
  const dispatch = useDispatch();

  const workspaceId = storeUser?.currentWorkspaceId;
  const userId = storeUser?.id;

  const [statusText, setStatusText] = useState<string>("Loading...");
  const [showProgress, setShowProgress] = useState<boolean>(true);
  const [hasAttemptedAuth, setHasAttemptedAuth] = useState<boolean>(false);

  const codeFromSlack = new URLSearchParams(window.location.search).get("code");
  const navigate = useNavigate();

  console.log("[SlackLanding] Component render", {
    hasStoreUser: !!storeUser,
    userId,
    workspaceId,
    hasCode: !!codeFromSlack,
    hasAttemptedAuth,
    pathname: window.location.pathname,
    search: window.location.search,
  });

  const authSlack = useCallback(
    async (code: string, userId: string, workspaceId: string) => {
      console.log("[SlackLanding] Starting Slack auth", {
        code: code.substring(0, 10) + "...",
        userId,
        workspaceId,
      });

      try {
        const result = await apiSlackAuth(code, userId, workspaceId);
        console.log("[SlackLanding] Auth API result:", result);

        if (result) {
          setStatusText("Slack is Authed!");
          const apiUser = await apiGetUser(userId);
          console.log("[SlackLanding] User after auth:", {
            userId: apiUser?.id,
            slackUserId: apiUser?.slackUserId,
          });
          dispatch(setUser(apiUser));
          navigate("/waz/settings/workspaces");
        } else {
          console.error("[SlackLanding] Slack auth returned false");
          setStatusText("Slack auth failed! 😢");
          setShowProgress(false);
          toast("Slack auth failed!", toastFail);
        }
      } catch (error) {
        console.error("[SlackLanding] Error during Slack auth:", error);
        setStatusText("Slack auth error! 😢");
        setShowProgress(false);
        toast("Slack auth error: " + (error as Error).message, toastFail);
      }
    },
    [dispatch, navigate]
  );

  useEffect(() => {
    console.log("[SlackLanding] useEffect triggered", {
      codeFromSlack: codeFromSlack
        ? codeFromSlack.substring(0, 10) + "..."
        : null,
      userId,
      workspaceId,
      hasStoreUser: !!storeUser,
      hasAttemptedAuth,
      fullUrl: window.location.href,
    });

    // Don't do anything if we've already attempted auth
    if (hasAttemptedAuth) {
      console.log("[SlackLanding] Already attempted auth, skipping");
      return;
    }

    // Check if we have the code
    if (!codeFromSlack) {
      console.error("[SlackLanding] No code from Slack redirect");
      setStatusText("Missing code from Slack redirect");
      setShowProgress(false);
      toast("Slack auth failed - no code received!", toastFail);
      setTimeout(() => navigate("/waz/settings/workspaces"), 2000);
      setHasAttemptedAuth(true);
      return;
    }

    // Wait for user data to load if it's not available yet
    if (!storeUser || !userId || !workspaceId) {
      console.log("[SlackLanding] Waiting for user data to load...", {
        hasStoreUser: !!storeUser,
        hasUserId: !!userId,
        hasWorkspaceId: !!workspaceId,
      });
      setStatusText("Loading user information...");
      // Don't mark as attempted yet - let it retry when storeUser loads
      return;
    }

    // We have everything we need - proceed with auth
    console.log(
      "[SlackLanding] All required data available, proceeding with auth"
    );
    setHasAttemptedAuth(true);
    authSlack(codeFromSlack, userId, workspaceId);
    trackAnalyticsEvent(TRACK_EVENTS.CompletedSlackIntegration, {}, storeUser);
  }, [
    codeFromSlack,
    userId,
    workspaceId,
    storeUser,
    hasAttemptedAuth,
    authSlack,
    navigate,
  ]);

  return (
    <div id="slack-landing-page">
      <div className="slack-landing-page-body">
        <div className="slack-landing-page-text">
          <h1>We're working on Authing Slack for you!</h1>
        </div>
        <div className="slack-landing-status-text">Status: {statusText}</div>

        {showProgress && <CircularProgress />}
        {!showProgress && (
          <div className="slack-landing-status-button">
            <Button
              variant="primary"
              onClick={() => navigate("/waz/settings/personal")}>
              Go To Settings
            </Button>

            {/* <button onClick={() => navigate("/waz/settings/personal")}>
              Go To Settings
            </button> */}
          </div>
        )}
      </div>
    </div>
  );
}
